import { useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { AwpRule, RuleEdit, updateRule } from "./rulesApi";

interface AwpRuleDetailProps {
  rule: AwpRule;
  readOnly?: boolean;
  onSaved: (rule: AwpRule) => void;
  /** Asks the page to confirm and remove the rule — the confirmation dialog lives there. */
  onDelete: (rule: AwpRule) => void;
  deleting?: boolean;
}

const TYPES: AwpRule["packageTypes"] = ["CWP", "EWP", "PWP"];
const METHODS = ["Deterministic", "Relational", "Cross-document", "Spatial", "Anomaly", "AI semantic", "Simulation", "Hybrid"];
const SEVERITIES = ["Critical", "Major", "Moderate", "Minor", "Advisory"];
const GATES = ["DQ0", "DQ1", "DQ2", "DQ3"];

/** A select that keeps an unexpected imported value visible rather than quietly replacing it. */
const pickList = (values: string[], current: string) =>
  current && !values.includes(current) ? [current, ...values] : values;

/**
 * The expanded row of one rule: its full requirement and evidence, and — for an administrator — an
 * edit and a delete. Editing happens in place so a rule can be corrected without re-importing the
 * document it came from. The code stays fixed; it is how findings and other layers refer to the rule.
 */
const AwpRuleDetail = ({ rule, readOnly = false, onSaved, onDelete, deleting = false }: AwpRuleDetailProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RuleEdit>(() => toDraft(rule));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof RuleEdit>(key: K, value: RuleEdit[K]) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await updateRule(rule.ruleSetId, rule.id, draft);
      onSaved(saved);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the rule.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="vr-awp-rule-detail">
        <div className="vr-awp-rule-detail-head">
          <div>
            <div className="vr-pkg-col-head">Requirement</div>
            {rule.requirement}
          </div>
          {!readOnly && (
            <div className="vr-cell-actions">
              <button
                className="vr-rowbtn vr-icononly vr-edit-amber"
                onClick={() => {
                  setDraft(toDraft(rule));
                  setEditing(true);
                }}
                disabled={deleting}
                title="Edit this rule"
                aria-label="Edit rule"
              >
                <Pencil size={13} />
              </button>
              <button
                className="vr-rowbtn vr-icononly vr-danger"
                onClick={() => onDelete(rule)}
                disabled={deleting}
                title="Remove this rule"
                aria-label="Remove rule"
              >
                {deleting ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
              </button>
            </div>
          )}
        </div>
        {rule.evidence && (
          <div>
            <div className="vr-pkg-col-head">Required evidence / output</div>
            {rule.evidence}
          </div>
        )}
        {(rule.version ?? 1) > 1 && (
          <div className="vr-cell-code">
            Edited — version {rule.version}
            {rule.editedAt ? ` · ${new Date(rule.editedAt).toLocaleDateString()}` : ""}. No longer the
            document's wording.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="vr-awp-rule-detail vr-awp-rule-edit">
      <div className="vr-field">
        <label>Title</label>
        <input className="vr-input" value={draft.title} onChange={(e) => set("title", e.target.value)} />
      </div>
      <div className="vr-field">
        <label>Requirement</label>
        <textarea
          className="vr-input vr-textarea"
          rows={3}
          value={draft.requirement}
          onChange={(e) => set("requirement", e.target.value)}
        />
      </div>
      <div className="vr-awp-rule-edit-grid">
        <div className="vr-field">
          <label>Method</label>
          <select className="vr-select" value={draft.method} onChange={(e) => set("method", e.target.value)}>
            {pickList(METHODS, draft.method).map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="vr-field">
          <label>Severity</label>
          <select className="vr-select" value={draft.severity} onChange={(e) => set("severity", e.target.value)}>
            {pickList(SEVERITIES, draft.severity).map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="vr-field">
          <label>Gate</label>
          <select className="vr-select" value={draft.gate} onChange={(e) => set("gate", e.target.value)}>
            {pickList(GATES, draft.gate).map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
        </div>
        <div className="vr-field">
          <label>Package types</label>
          <div className="vr-awp-type-picks">
            {TYPES.map((t) => (
              <label key={t} className="vr-awp-type-pick">
                <input
                  type="checkbox"
                  checked={draft.packageTypes.includes(t)}
                  onChange={() =>
                    set(
                      "packageTypes",
                      draft.packageTypes.includes(t)
                        ? draft.packageTypes.filter((x) => x !== t)
                        : TYPES.filter((x) => x === t || draft.packageTypes.includes(x)),
                    )
                  }
                />
                {t}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="vr-field">
        <label>Required evidence / output</label>
        <input className="vr-input" value={draft.evidence} onChange={(e) => set("evidence", e.target.value)} />
      </div>
      {error && <div className="vr-form-error">{error}</div>}
      <div className="vr-cell-actions">
        <button className="vr-btn vr-btn-ghost" onClick={() => setEditing(false)} disabled={saving}>
          Cancel
        </button>
        <button
          className="vr-btn vr-btn-glow"
          onClick={() => void save()}
          disabled={saving || !draft.title.trim() || !draft.requirement.trim() || draft.packageTypes.length === 0}
        >
          {saving ? (
            <>
              <Loader2 size={14} className="vr-spin-inline" /> Saving…
            </>
          ) : (
            "Save rule"
          )}
        </button>
      </div>
    </div>
  );
};

function toDraft(rule: AwpRule): RuleEdit {
  return {
    title: rule.title,
    requirement: rule.requirement,
    method: rule.method,
    severity: rule.severity,
    gate: rule.gate,
    evidence: rule.evidence,
    packageTypes: [...rule.packageTypes],
    _etag: rule._etag,
  };
}

export default AwpRuleDetail;
